import type { ReactNode } from 'react';
import { deriveVariants } from '@/lib/media';

/**
 * Custom MDX editorial components. Registered via MDXProvider in main.tsx
 * (or used directly in MDX with explicit imports). These give content authors
 * editorial primitives that fit BED's typography system.
 */

interface PullQuoteProps {
  children: ReactNode;
  attribution?: string;
}

export function PullQuote({ children, attribution }: PullQuoteProps) {
  return (
    <figure className="mdx-pullquote">
      <blockquote>{children}</blockquote>
      {attribution ? <figcaption className="caption">— {attribution}</figcaption> : null}
    </figure>
  );
}

interface FigureProps {
  src: string;
  alt: string;
  caption?: ReactNode;
  /** Make the figure span the full container width (wider than the body text). */
  wide?: boolean;
}

export function Figure({ src, alt, caption, wide = false }: FigureProps) {
  const v = deriveVariants(src);
  return (
    <figure className={`mdx-figure ${wide ? 'mdx-figure--wide' : ''}`}>
      <picture>
        {v.avif ? <source srcSet={v.avif} type="image/avif" /> : null}
        {v.webp ? <source srcSet={v.webp} type="image/webp" /> : null}
        <img src={v.fallback} alt={alt} loading="lazy" decoding="async" />
      </picture>
      {caption ? <figcaption className="caption">{caption}</figcaption> : null}
    </figure>
  );
}

interface TwoColumnProps {
  children: ReactNode;
}

export function TwoColumn({ children }: TwoColumnProps) {
  return <div className="mdx-twocol">{children}</div>;
}

interface StatlineProps {
  value: string;
  label: string;
}

export function Statline({ value, label }: StatlineProps) {
  return (
    <div className="mdx-statline">
      <span className="mdx-statline__value display-italic">{value}</span>
      <span className="eyebrow mdx-statline__label">{label}</span>
    </div>
  );
}

export function Ornament() {
  return <p className="ornament mdx-ornament" aria-hidden="true">§</p>;
}

/** Map for MDXProvider — global names available without explicit import in MDX. */
export const MDX_COMPONENTS = {
  PullQuote,
  Figure,
  TwoColumn,
  Statline,
  Ornament,
};
